import { AlertCircle, Check, ChevronDown, ChevronRight, Heart, PackageCheck, Search, ShoppingCart, SlidersHorizontal, Star, Truck, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BuyAgainProduct } from "./BuyAgainTypes";
import { mapBuyAgainCardToProduct } from "./BuyAgainTypes";
import "./BuyAgainPage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import { useCart } from "../../../context/CartContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { fetchBuyAgainProducts } from "../../../Redux/buyAgain/slice";

type BuyAgainRouteState = { sourceProductId?: string };
type SortValue = "recent" | "rating" | "price-low" | "price-high";
const formatPrice = (value: number) => new Intl.NumberFormat("en-IN").format(value);

const BuyAgainPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const routeState = location.state as BuyAgainRouteState | null;
    const { cartItems, addToCart } = useCart();

    // ─── Redux state ──────────────────────────────────────────────────────────
    const dispatch = useAppDispatch();
    const { cards, loading, enriching, enrichProgress, error, lastFetchedAt } =
        useAppSelector((s) => s.buyAgain);

    // Fetch on mount (5-minute cache to avoid re-fetching on re-renders)
    useEffect(() => {
        const cacheMaxAge = 5 * 60 * 1000;
        const isStale = !lastFetchedAt || Date.now() - lastFetchedAt > cacheMaxAge;
        if (isStale) dispatch(fetchBuyAgainProducts());
    }, [dispatch, lastFetchedAt]);

    // Map Redux cards to display shape
    const allProducts = useMemo(
        () => cards.map(mapBuyAgainCardToProduct),
        [cards],
    );

    // Derive categories from real data (replaces static buyAgainCategories)
    const availableCategories = useMemo(
        () => Array.from(new Set(allProducts.map((p) => p.category).filter(Boolean))),
        [allProducts],
    );

    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [dateRange, setDateRange] = useState("all");
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [priceRange, setPriceRange] = useState("all");
    const [minimumRating, setMinimumRating] = useState(0);
    const [dealsOnly, setDealsOnly] = useState(false);
    const [freeDeliveryOnly, setFreeDeliveryOnly] = useState(false);
    const [fastDeliveryOnly, setFastDeliveryOnly] = useState(false);
    const [inStockOnly, setInStockOnly] = useState(false);
    const [sort, setSort] = useState<SortValue>("recent");
    const [wishlist, setWishlist] = useState<string[]>([]);
    const [selectedColors, setSelectedColors] = useState<Record<string, number>>({});

    const toggleWishlist = (id: string) => {
        setWishlist((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
    };
    const selectColor = (productId: string, colorIndex: number) => {
        setSelectedColors((prev) => ({ ...prev, [productId]: colorIndex }));
    };
    const toggleCategory = (category: string) => {
        setSelectedCategories((prev) => prev.includes(category) ? prev.filter((item) => item !== category) : [...prev, category]);
    };

    const clearFilters = () => {
        setSearch("");
        setDateRange("all");
        setSelectedCategories([]);
        setPriceRange("all");
        setMinimumRating(0);
        setDealsOnly(false);
        setFreeDeliveryOnly(false);
        setFastDeliveryOnly(false);
        setInStockOnly(false);
        setSort("recent");
    };

    // All 8 filters work client-side on the merged card data
    const filteredProducts = useMemo(() => {
        const query = search.trim().toLowerCase();
        const filtered = allProducts.filter((product) => {
            const matchesSearch = !query ||
                product.title.toLowerCase().includes(query) ||
                product.description.toLowerCase().includes(query) ||
                product.category.toLowerCase().includes(query);
            const matchesDate =
                dateRange === "all" ||
                (dateRange === "30" && product.purchasedDaysAgo <= 30) ||
                (dateRange === "90" && product.purchasedDaysAgo <= 90) ||
                (dateRange === "180" && product.purchasedDaysAgo <= 180);
            const matchesCategory =
                selectedCategories.length === 0 ||
                selectedCategories.includes(product.category);
            const matchesPrice =
                priceRange === "all" ||
                (priceRange === "under-1500" && product.price < 1500) ||
                (priceRange === "1500-1800" && product.price >= 1500 && product.price <= 1800) ||
                (priceRange === "above-1800" && product.price > 1800);
            const matchesRating = product.rating >= minimumRating;
            const matchesDeals = !dealsOnly || Boolean(product.deal);
            const matchesFreeDelivery = !freeDeliveryOnly || product.deliveryType === "delivery";
            const matchesFastDelivery = !fastDeliveryOnly || product.deliveryType === "fast";
            const matchesStock = !inStockOnly || product.deliveryType !== "stock";
            return (
                matchesSearch &&
                matchesDate &&
                matchesCategory &&
                matchesPrice &&
                matchesRating &&
                matchesDeals &&
                matchesFreeDelivery &&
                matchesFastDelivery &&
                matchesStock
            );
        });
        const sorted = [...filtered].sort((a, b) => {
            if (sort === "rating") return b.rating - a.rating;
            if (sort === "price-low") return a.price - b.price;
            if (sort === "price-high") return b.price - a.price;
            return a.purchasedDaysAgo - b.purchasedDaysAgo;
        });
        // Pin the source product to the top if navigated from a "Buy it again" button
        const targetId = routeState?.sourceProductId;
        if (!targetId) return sorted;
        return [
            ...sorted.filter((product) => product.id === targetId),
            ...sorted.filter((product) => product.id !== targetId),
        ];
    }, [
        allProducts,
        search,
        dateRange,
        selectedCategories,
        priceRange,
        minimumRating,
        dealsOnly,
        freeDeliveryOnly,
        fastDeliveryOnly,
        inStockOnly,
        sort,
        routeState?.sourceProductId,
    ]);

    // Fix: navigate to the existing product detail route (was /product/:id — broken)
    const handleProductClick = (product: BuyAgainProduct) => {
        navigate(`/DetailPage/${product.id}`);
    };

    const handleAddToCart = (product: BuyAgainProduct) => {
        const isAdded = cartItems.some((item: any) => item.productId === product.id);
        if (isAdded) return;
        const colorIndex = selectedColors[product.id] ?? 0;
        const color = product.colors[colorIndex];
        addToCart({
            productId: product.id,        // MongoDB ObjectId string
            title: product.title,
            description: product.description,
            image: product.image,
            price: product.price,
            mrp: product.mrp,
            color: color?.name,
            variant: color?.name,
        });
    };

    // Retry handler for error state
    const handleRetry = () => dispatch(fetchBuyAgainProducts());

    return (
        <>
            <HomeNavbar variant="home" />
            <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() =>
                setBrowsingHistoryOpen((current) => !current)} />
            <main className="buy-again-page">
                <div className="buy-again-shell">
                    <div className="buy-again-breadcrumb">
                        <button type="button" onClick={() => navigate("/Orders")}>
                            Your Orders
                        </button>
                        <ChevronRight size={15} />
                        <strong>Buy Again</strong>
                    </div>
                    <section className="buy-again-hero">
                        <div>
                            <span className="buy-again-eyebrow">YOUR PURCHASE HISTORY</span>
                            <h1>Buy Again</h1>
                            <p>Reorder products from your previous Salar purchases and quickly add them back to your cart.</p>
                        </div>
                        <label className="buy-again-search">
                            <Search size={20} />
                            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search your previous purchases" />
                        </label>
                    </section>
                    <div className="buy-again-content">
                        <aside className="buy-again-filter-panel">
                            <div className="buy-again-filter-heading">
                                <div>
                                    <SlidersHorizontal size={20} />
                                    <strong>Filters</strong>
                                </div>
                                <button type="button" onClick={clearFilters}> Clear all </button>
                            </div>
                            <div className="buy-again-filter-section">
                                <h2>Previously Ordered</h2>
                                {[["all", "All purchases"], ["30", "Last 30 days"], ["90", "Last 3 months"], ["180", "Last 6 months"]].map(([value, label]) => (
                                    <label className="buy-again-filter-option" key={value}>
                                        <input type="radio" name="buy-again-date" checked={dateRange === value} onChange={() => setDateRange(value)} />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="buy-again-filter-section">
                                <h2>Category</h2>
                                {availableCategories.map((category) => (
                                    <label className="buy-again-filter-option" key={category}>
                                        <input type="checkbox" checked={selectedCategories.includes(category)} onChange={() => toggleCategory(category)} />
                                        <span>{category}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="buy-again-filter-section">
                                <h2>Price</h2>
                                {[["all", "All prices"], ["under-1500", "Under ₹1,500"], ["1500-1800", "₹1,500 – ₹1,800"], ["above-1800", "Above ₹1,800"],].map(([value, label]) => (
                                    <label className="buy-again-filter-option" key={value}>
                                        <input type="radio" name="buy-again-price" checked={priceRange === value} onChange={() => setPriceRange(value)} />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="buy-again-filter-section">
                                <h2>Customer Reviews</h2>
                                {[4.5, 4].map((value) => (
                                    <button type="button" className={`buy-again-rating-filter ${minimumRating === value ? "active" : ""}`} key={value} onClick={() => setMinimumRating(minimumRating === value ? 0 : value)}>
                                        <span className="buy-again-filter-stars">
                                            {[1, 2, 3, 4, 5].map((star) => (<Star key={star} size={14} fill={star <= Math.round(value) ? "currentColor" : "none"} />))}
                                        </span>
                                        <span>{value.toFixed(1)} & above</span>
                                    </button>
                                ))}
                            </div>
                            <div className="buy-again-filter-section">
                                <h2>Shopping Options</h2>
                                <label className="buy-again-filter-option">
                                    <input type="checkbox" checked={dealsOnly} onChange={(event) => setDealsOnly(event.target.checked)} />
                                    <span>Deals & Discounts</span>
                                </label>
                                <label className="buy-again-filter-option">
                                    <input type="checkbox" checked={freeDeliveryOnly} onChange={(event) => setFreeDeliveryOnly(event.target.checked)} />
                                    <span>Free Delivery</span>
                                </label>
                                <label className="buy-again-filter-option">
                                    <input type="checkbox" checked={fastDeliveryOnly} onChange={(event) => setFastDeliveryOnly(event.target.checked)} />
                                    <span>Fast Delivery</span>
                                </label>
                                <label className="buy-again-filter-option">
                                    <input type="checkbox" checked={inStockOnly} onChange={(event) => setInStockOnly(event.target.checked)} />
                                    <span>In Stock</span>
                                </label>
                            </div>
                        </aside>
                        <section className="buy-again-results">
                            <div className="buy-again-results-head">
                                <div>
                                    <span>REORDER PICKS</span>
                                    <h2>Products you've purchased</h2>
                                    <p>
                                        {loading
                                            ? "Loading your purchase history…"
                                            : enriching
                                                ? `Loading live prices… (${enrichProgress.done}/${enrichProgress.total})`
                                                : `${filteredProducts.length} products available to buy again`}
                                    </p>
                                </div>
                                <label className="buy-again-sort">
                                    <span>Sort by</span>
                                    <div>
                                        <select value={sort} onChange={(event) => setSort(event.target.value as SortValue)}>
                                            <option value="recent">Recently ordered</option>
                                            <option value="rating">Customer rating</option>
                                            <option value="price-low">Price: Low to High</option>
                                            <option value="price-high">Price: High to Low</option>
                                        </select>
                                        <ChevronDown size={16} />
                                    </div>
                                </label>
                            </div>

                            {/* Error state */}
                            {error && allProducts.length === 0 ? (
                                <div className="buy-again-empty">
                                    <AlertCircle size={28} />
                                    <h3>Something went wrong</h3>
                                    <p>{error}</p>
                                    <button type="button" onClick={handleRetry}> Try again </button>
                                </div>
                            ) : /* Initial loading skeleton */ loading && allProducts.length === 0 ? (
                                <div className="buy-again-product-grid">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="men-product-card" style={{
                                            background: "#f0f3f7",
                                            animation: "categoryPulse 1.2s ease-in-out infinite",
                                            minHeight: "380px",
                                            borderRadius: "10px",
                                        }} />
                                    ))}
                                </div>
                            ) : /* Empty state — no delivered orders */ allProducts.length === 0 ? (
                                <div className="buy-again-empty">
                                    <Search size={28} />
                                    <h3>No previous purchases to reorder yet</h3>
                                    <p>Products from your delivered orders will appear here for quick reordering.</p>
                                </div>
                            ) : filteredProducts.length > 0 ? (
                                <div className="buy-again-product-grid">
                                    {filteredProducts.map((product) => {
                                        const discount = product.mrp > product.price
                                            ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
                                            : 0;
                                        const isWishlisted = wishlist.includes(product.id);
                                        const selectedColor = selectedColors[product.id] ?? 0;
                                        const isAdded = cartItems.some((item: any) => item.productId === product.id);
                                        const isTarget = routeState?.sourceProductId === product.id;
                                        return (
                                            <article className={`men-product-card ${isTarget ? "buy-again-target-product" : ""}`} key={product.id} role="button" tabIndex={0} onClick={(event) => { const target = event.target as HTMLElement; if (target.closest("button")) return; handleProductClick(product); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); handleProductClick(product); } }} >
                                                <div className="men-product-image-wrapper">
                                                    <span className="men-product-badge"> {product.badge} </span>
                                                    <button type="button" className={`men-product-wishlist ${isWishlisted ? "active" : ""}`} onClick={() => toggleWishlist(product.id)}
                                                        aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"} >
                                                        <Heart size={18} strokeWidth={2} fill={isWishlisted ? "currentColor" : "none"} />
                                                    </button>
                                                    <img src={product.image} alt={product.title} className="men-product-image"
                                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/assets/images/logos/salarlogo.png"; }} />
                                                </div>
                                                <div className="men-product-info">
                                                    {product.colors.length > 0 && (
                                                        <div className="men-product-colors">
                                                            <div className="men-product-color-list">
                                                                {product.colors.slice(0, 5).map((color, index) => (
                                                                    <button type="button" key={color.name} className={`men-product-color ${selectedColor === index ? "active" : ""}`} title={color.name} aria-label={`Select ${color.name}`} onClick={() => selectColor(product.id, index)}>
                                                                        <span style={{ backgroundColor: color.value }} />
                                                                    </button>
                                                                ))}
                                                                {product.extraColors > 0 && (
                                                                    <button type="button" className="men-product-more-colors" onClick={() => handleProductClick(product)} >
                                                                        +{product.extraColors} more
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <h3>{product.title}</h3>
                                                    <p className="men-product-description">{product.description || product.category}</p>
                                                    <div className="men-product-rating">
                                                        {product.rating > 0 ? (
                                                            <>
                                                                <strong>{product.rating.toFixed(1)}</strong>
                                                                <span className="men-product-stars">
                                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                                        <Star key={star} size={13} strokeWidth={1.8} fill={star <= Math.round(product.rating) ? "currentColor" : "none"} />))}
                                                                </span>
                                                                <span className="men-product-review-count">({product.reviews})</span>
                                                            </>
                                                        ) : (
                                                            <span className="men-product-stars">
                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                    <Star key={star} size={13} strokeWidth={1.8} fill="none" />))}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {product.deal && (
                                                        <div className={`men-product-deal ${product.dealType}`}>
                                                            <span className="men-product-deal-dot" />
                                                            <strong>{product.deal}</strong>
                                                        </div>
                                                    )}
                                                    <div className="men-product-pricing">
                                                        <div className="men-product-price-main">
                                                            <strong><sup>₹</sup>{formatPrice(product.price)}</strong>
                                                            {discount > 0 && (
                                                                <span className="men-product-discount">{discount}% OFF</span>
                                                            )}
                                                        </div>
                                                        {product.mrp > product.price && (
                                                            <div className="men-product-price-secondary">
                                                                <span>MRP</span>
                                                                <del>₹{formatPrice(product.mrp)}</del>
                                                                <span className="men-product-saving">Save ₹{formatPrice(product.mrp - product.price)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`men-product-delivery ${product.deliveryType}`}>
                                                        {product.deliveryType === "delivery" && (<Truck size={15} strokeWidth={1.9} />)}
                                                        {product.deliveryType === "fast" && (<Zap size={15} strokeWidth={2} />)}
                                                        {product.deliveryType === "stock" && (<PackageCheck size={15} strokeWidth={1.9} />)}
                                                        <span>{product.delivery}</span>
                                                    </div>
                                                    <button type="button" className={`men-product-options ${isAdded ? "added" : ""}`} onClick={() => handleAddToCart(product)}>
                                                        {isAdded ? (
                                                            <><Check size={16} strokeWidth={2.4} /> Added to Cart</>
                                                        ) : (
                                                            <><ShoppingCart size={16} strokeWidth={2} /> Add to Cart</>
                                                        )}
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="buy-again-empty">
                                    <Search size={28} />
                                    <h3>No matching purchases</h3>
                                    <p>Try clearing one or more filters to see your previous products.</p>
                                    <button type="button" onClick={clearFilters}> Clear Filters </button>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </main>
        </>
    );
};

export default BuyAgainPage;
